import { Form, Formik, FormikProps } from "formik"
import React, { Dispatch, SetStateAction, useMemo, useRef } from "react"
import * as Yup from "yup"

import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { TipType } from "@mono/essentials/types/CartTypes"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import { PayOrderRequestType } from "@mono/essentials/types/OrderTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"

import TippingFormData from "./TippingFormData"

export type TippingFormAttributes = {
  tip: number
  tip_status: string
}
export interface TippingFormProps {
  tipEnabled?: boolean
  cartId?: string
  orderId?: string
  onSuccess?: (tip?: PayOrderRequestType["tip"]) => Promise<void> | void
  goToNextStep?: () => void
  handleClose: () => void
  tipType?: TipType
  isCustomTippingAllowed?: boolean
  isModal?: boolean
  cartDetails: CartItemDetailsType
  initialValues?: {
    totalTip: string
    tipType: TipType
    tipValue: string
  }
  defaultTipOptions?: SelectOptionType[]
  onlyTipPayment?: boolean
  setTipSubmitting?: Dispatch<SetStateAction<boolean>>
}

export interface TippingFormProps {
  isWidgetsCard: boolean
  domainDetails: IDomain
  tipData: {
    tip_number: string
    tip_options: {
      amount: number
    }[]
    tip_notes: string
    tip_type: string
    is_custom_tip_allowed?: boolean
  }
}

const TippingForm = ({
  tipData,
  domainDetails,
  isWidgetsCard,
  onSuccess,
  tipType,
  cartDetails,
  cartId,
  tipInitialValues: formInitialValues,
  onlyTipPayment = false,
  isIntegratedPage = false,
  setTipSubmitting,
  paymentAmountDetails,
}: any) => {
  const formikRef = useRef<FormikProps<TippingFormAttributes>>(null)
  const initialValues: TippingFormAttributes = useMemo(
    () => {
      const isTipIncluded = (tipData?.tip_options || []).some(
        (item) => parseFloat(item.amount).toFixed(2) === formInitialValues?.tipValue
      )
      return {
        tip: formInitialValues?.tipValue ? formInitialValues.tipValue : 0,
        tip_status: formInitialValues?.tipValue
          ? `${isTipIncluded ? `fixed-${formInitialValues.tipValue}` : `custom`}`
          : "fixed",
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        tip: Yup.number().required("*Tip amount is Required").min(0),
      }),
    []
  )

  const handleTipping = async (tip: number, tipType: TipType) => {
    try {
      if (cartId) {
        setTipSubmitting?.(true)
        await OrderServices.updateCart(
          cartId,
          {
            ...cartDetails,
            tip: {
              source: "customer",
              type: tipType,
              value: tip,
            },
          },
          isIntegratedPage
        )
        await onSuccess?.()
      }
    } catch (e) {
      console.log("Apply tip error: ", e)
    } finally {
      setTipSubmitting?.(false)
    }
  }

  const removeTipping = async () => {
    try {
      setTipSubmitting?.(true)
      if (cartId) {
        await OrderServices.updateCart(
          cartId,
          {
            ...cartDetails,
          },
          isIntegratedPage
        )
        await onSuccess?.()
        formikRef?.current?.resetForm()
      }
    } catch (e) {
      console.log("Apply tip error: ", e)
    } finally {
      setTipSubmitting?.(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        if (onlyTipPayment) {
          setSubmitting(true)
          await handleTipping(values.tip, formInitialValues?.tipType ?? tipType ?? "percent")
          setSubmitting(false)
        }
      }}
      validateOnChange
      enableReinitialize
      innerRef={formikRef}
    >
      {() => (
        <Form autoComplete={"off"}>
          <TippingFormData
            isWidgetsCard={isWidgetsCard}
            domainDetails={domainDetails}
            tipData={tipData}
            handleTipping={handleTipping}
            formattedTotalTip={formInitialValues?.totalTip}
            removeTipping={removeTipping}
            paymentAmountDetails={paymentAmountDetails}
          />
        </Form>
      )}
    </Formik>
  )
}

export default TippingForm
